// src/pages/imamMahdi/HowToTalk.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const HowToTalk = () => {
  return (
    <>
      <Helmet>
        <title>How to Talk to Imam Mahdi (aj) | Spiritual Connection Guide</title>
        <meta
          name="description"
          content="Learn how to speak to Imam Mahdi (aj) with sincerity and love. A gentle guide to whispered prayers, loyalty, and personal reflection for believers."
        />
        <meta property="og:title" content="How to Talk to Imam Mahdi (aj) | Spiritual Connection Guide" />
        <meta
          property="og:description"
          content="Understand how to communicate with Imam Mahdi (aj) in prayer and reflection. Discover heartfelt ways to express love, loyalty, and devotion."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/ningth-How-to-talk-to-Imam-Mahdi-today-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">How To Talk to Imam Mahdi</h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/ningth-How-to-talk-to-Imam-Mahdi-today-.png"
              alt="How to Talk to Imam Mahdi"
              className="imam-image"
            />

            <h2>A Gentle Guide to Personal Whispered Prayers</h2>
            <p>
              <strong>A Living, Caring Imam:</strong> In belief, Imam Mahdi (peace be upon him) is not a
              distant or absent figure. He is alive by Allah’s will, aware of the state of his followers,
              praying for them, and quietly guiding hearts towards goodness.
            </p>
            <p>
              This means it is entirely natural—and spiritually nourishing—to speak to him in your own
              words, sharing your struggles, hopes, and pledges of loyalty.
            </p>

            <h2>What Does It Mean to “Talk” to the Imam?</h2>
            <p>
              It simply means opening your heart in a private, whispered conversation (munajat). You might
              sit on your prayer mat, stand by your window, or even speak to him in your heart as you walk.
            </p>
            <p>
              Many Shia scholars recommend speaking to Imam Mahdi the way you would speak to a compassionate
              elder brother or a deeply loving guide: with honesty, vulnerability, and trust.
            </p>

            <h2>A Gentle Guide to Your Personal Whispered Prayers</h2>
            <ol>
              <li>
                <strong>Start by Sending Salaams:</strong> Begin by greeting him respectfully:
                <blockquote>
                  “Peace be upon you, O remnant of Allah on His earth. Peace be upon you, O awaited Imam.”
                </blockquote>
                This builds the sense that he is truly there, receiving your words.
              </li>
              <li>
                <strong>Express Your Longing & Loyalty:</strong> Tell him how much you long for his
                appearance, how tired the world is under injustice, and how you hope to stand with him one
                day.
              </li>
              <li>
                <strong>Share Your Struggles:</strong> There is nothing too small. Whether it’s your worries
                about family, your fight against a personal flaw, or concern for the suffering of others—
                speak it sincerely.
              </li>
              <li>
                <strong>Renew Your Pledge:</strong> End by affirming your commitment:
                <blockquote>
                  “My Imam, I pledge to strive for truth and justice today, so I may be worthy to stand under
                  your banner when Allah commands your appearance.”
                </blockquote>
              </li>
              <li>
                <strong>Pray for Him:</strong> Tradition encourages praying for the Imam’s protection and
                ease, asking Allah to keep him safe from harm until the appointed time.
              </li>
            </ol>

            <h3>A Small Example of Whispered Words</h3>
            <blockquote>
              “O my Imam, I know I fall short, but my heart loves you. Help me become the person who would
              make you proud – honest, brave, gentle with the weak, firm against injustice. Guide my steps
              until I am ready to greet you.”
            </blockquote>

            <h3>Why It Matters</h3>
            <p>
              Talking to Imam Mahdi (aj) today is one of the simplest, most intimate acts of faith. It keeps
              your heart connected to him, reminds you that you’re never alone, and strengthens the bond that
              will one day lead you to his side when he reappears.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/described-in-quran-hadith">
                Described In Quran & Hadith
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">Impact of His Appearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">Practices & Allegiance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">Reflections & Stories</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">Signs of Reappearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default HowToTalk;
